import {match, Pattern} from 'ts-pattern';

import type {FinancialProductQuote} from '../types/FinancialProductQuote';
import type {FinancialProductQuoteCoordinates} from '../types/FinancialProductQuoteCoordinates';
import type {FinancialProductQuoteEnvelope} from '../types/FinancialProductQuoteEnvelope';
import type {FinancialProductQuoteOffer} from '../types/FinancialProductQuoteOffer';
import {canComputeFinancialProductQuoteLocally} from './canComputeFinancialProductQuoteLocally';
import {resolveFinancingQuote} from './resolveFinancingQuote';
import {resolveLeasingQuote} from './resolveLeasingQuote';

/**
 * The quote the backend would answer for these coordinates, computed from the published inputs.
 * Null when the offer cannot be priced locally (see `canComputeFinancialProductQuoteLocally`),
 * the coordinates belong to another product, or they are incomplete or invalid.
 */
export function getLocalFinancialProductQuote(
  envelope: FinancialProductQuoteEnvelope,
  offer: FinancialProductQuoteOffer,
  coordinates: FinancialProductQuoteCoordinates
): FinancialProductQuote | null {
  return match({
    isComputable:
      coordinates.productType === offer.productType &&
      canComputeFinancialProductQuoteLocally(envelope, offer),
    productType: offer.productType,
    vehiclePriceWithVat: envelope.vehiclePriceWithVat,
  })
    .returnType<FinancialProductQuote | null>()
    .with({isComputable: false}, () => null)
    .with({productType: 'LEASING'}, () => resolveLeasingQuote(offer, coordinates))
    .with({vehiclePriceWithVat: Pattern.nonNullable}, (financing) =>
      resolveFinancingQuote(financing.vehiclePriceWithVat, offer, coordinates)
    )
    .otherwise(() => null);
}
